/**
 * WarehouseSuppliers
 * Alt navigasyonda Inventur'un yanında gösterilecek bağımsız tedarikçi yönetim sayfası.
 * Admin/warehouse kullanıcıları burada tedarikçi ekle/düzenle/sil yapabilir.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Truck, Search, Plus, Trash2, Pencil, Save,
  Phone, Mail, Clock, MapPin, Globe, CreditCard,
} from 'lucide-react';

const EMPTY_FORM = {
  name: '', strasse: '', plz: '', ort: '', land: '',
  email: '', phone: '', delivery_time: '', zahlungsziel: '',
};

export default function WarehouseSuppliers() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => base44.entities.Supplier.list(),
    refetchOnWindowFocus: true,
  });

  const createMut = useMutation({
    mutationFn: (d) => base44.entities.Supplier.create(d),
    onSuccess: () => { queryClient.invalidateQueries(['suppliers']); toast.success('Lieferant erstellt'); closeModal(); },
    onError: (e) => toast.error('Fehler: ' + e.message),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Supplier.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries(['suppliers']); toast.success('Lieferant aktualisiert'); closeModal(); },
    onError: (e) => toast.error('Fehler: ' + e.message),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => base44.entities.Supplier.delete(id),
    onSuccess: () => { queryClient.invalidateQueries(['suppliers']); toast.success('Lieferant gelöscht'); },
    onError: (e) => toast.error('Fehler: ' + e.message),
  });

  const closeModal = () => { setModalOpen(false); setEditing(null); setForm({ ...EMPTY_FORM }); };

  const openModal = (s = null) => {
    if (s) {
      setEditing(s);
      setForm({
        name: s.name || '', strasse: s.strasse || '', plz: s.plz || '',
        ort: s.ort || '', land: s.land || '', email: s.email || '',
        phone: s.phone || '', delivery_time: s.delivery_time || '',
        zahlungsziel: s.zahlungsziel || '',
      });
    } else {
      setEditing(null);
      setForm({ ...EMPTY_FORM });
    }
    setModalOpen(true);
  };

  const handleSubmit = () => {
    if (!form.name.trim()) { toast.error('Bitte geben Sie einen Namen ein.'); return; }
    if (editing) { updateMut.mutate({ id: editing.id, data: form }); }
    else { createMut.mutate({ ...form, is_active: true }); }
  };

  const f = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  const filtered = suppliers.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.ort || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.email || '').toLowerCase().includes(search.toLowerCase())
  );

  // Build address display string
  const addressLine = (s) => {
    const parts = [s.strasse, [s.plz, s.ort].filter(Boolean).join(' '), s.land].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <PageShell
      head={(
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PageHeader icon={<Truck className="w-5 h-5" />} title="Lieferanten" />
              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 ml-1">{suppliers.length}</Badge>
            </div>
            <Button onClick={() => openModal()} className="bg-amber-600 hover:bg-amber-700 text-white gap-2 font-bold shadow-lg h-11 px-5 rounded-xl">
              <Plus className="w-5 h-5" /> Neuer Lieferant
            </Button>
          </div>
      )}
    >

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Lieferant suchen..." className="pl-9 h-10" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {/* Cards */}
      {isLoading ? (
        <div className="text-center py-16 text-slate-400">Lade Lieferanten...</div>
      ) : filtered.length === 0 ? (
        <Card className="border-2 border-dashed">
          <CardContent className="p-12 text-center text-slate-400">
            <Truck className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p className="font-medium">Keine Lieferanten vorhanden.</p>
            <p className="text-xs mt-1">Klicken Sie auf "+" um einen Lieferanten anzulegen.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map(s => (
            <Card key={s.id} className="hover:shadow-md transition-shadow group relative">
              <CardContent className="p-4 space-y-3">
                {/* Header row */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0 border border-amber-200">
                      <Truck className="w-4.5 h-4.5 text-amber-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-slate-900 truncate text-sm">{s.name}</h3>
                      {addressLine(s) && <p className="text-[10px] text-slate-500 truncate">{addressLine(s)}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openModal(s)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => { if (confirm(`Lieferant "${s.name}" wirklich löschen?`)) deleteMut.mutate(s.id); }} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>

                {/* Info grid */}
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[10px]">
                  {s.phone && <div className="flex items-center gap-1.5 text-slate-600"><Phone className="w-3 h-3 text-slate-400 shrink-0" /><span className="truncate">{s.phone}</span></div>}
                  {s.email && <div className="flex items-center gap-1.5 text-slate-600"><Mail className="w-3 h-3 text-slate-400 shrink-0" /><span className="truncate">{s.email}</span></div>}
                  {s.delivery_time && <div className="flex items-center gap-1.5 text-slate-600"><Clock className="w-3 h-3 text-slate-400 shrink-0" /><span>{s.delivery_time}</span></div>}
                  {s.zahlungsziel && <div className="flex items-center gap-1.5 text-slate-600"><CreditCard className="w-3 h-3 text-slate-400 shrink-0" /><span>{s.zahlungsziel}</span></div>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ========== Create/Edit Modal ========== */}
      <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Truck className="w-5 h-5 text-amber-600" />
              {editing ? 'Lieferant bearbeiten' : 'Neuer Lieferant'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {/* Name */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold">Name *</Label>
              <Input placeholder="z.B. Metro, Selgros..." value={form.name} onChange={f('name')} className="h-11 font-medium" autoFocus />
            </div>

            {/* Adresse Block */}
            <div className="rounded-lg border border-slate-200 p-3 space-y-3 bg-slate-50/50">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <MapPin className="w-3 h-3" /> Adresse
              </p>
              <div className="space-y-2.5">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold text-slate-500">Straße</Label>
                  <Input placeholder="Musterstraße 12" value={form.strasse} onChange={f('strasse')} className="h-9 text-xs" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-slate-500">PLZ</Label>
                    <Input placeholder="12345" value={form.plz} onChange={f('plz')} className="h-9 text-xs" />
                  </div>
                  <div className="col-span-2 space-y-1">
                    <Label className="text-[10px] font-bold text-slate-500">Ort</Label>
                    <Input placeholder="Berlin" value={form.ort} onChange={f('ort')} className="h-9 text-xs" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold text-slate-500">Land</Label>
                  <div className="relative">
                    <Globe className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-300" />
                    <Input placeholder="Deutschland" value={form.land} onChange={f('land')} className="h-9 text-xs pl-8" />
                  </div>
                </div>
              </div>
            </div>

            {/* Kontakt */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold">E-Mail</Label>
                <div className="relative">
                  <Mail className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-300" />
                  <Input type="email" placeholder="email@firma.de" value={form.email} onChange={f('email')} className="h-9 text-xs pl-8" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold">Telefon</Label>
                <div className="relative">
                  <Phone className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-300" />
                  <Input placeholder="+49 ..." value={form.phone} onChange={f('phone')} className="h-9 text-xs pl-8" />
                </div>
              </div>
            </div>

            {/* Konditionen */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold">Lieferzeit</Label>
                <div className="relative">
                  <Clock className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-300" />
                  <Input placeholder="z.B. 3-5 Tage" value={form.delivery_time} onChange={f('delivery_time')} className="h-9 text-xs pl-8" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold">Zahlungsziel</Label>
                <div className="relative">
                  <CreditCard className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-300" />
                  <Input placeholder="z.B. 30 Tage netto" value={form.zahlungsziel} onChange={f('zahlungsziel')} className="h-9 text-xs pl-8" />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={closeModal} className="h-10">Abbrechen</Button>
            <Button onClick={handleSubmit} disabled={createMut.isPending || updateMut.isPending} className="bg-amber-600 hover:bg-amber-700 text-white gap-2 font-bold h-10 px-6">
              <Save className="w-4 h-4" />
              {editing ? 'Aktualisieren' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
